import { useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { CONTACT, NOTES, SATELLITES, SCENES } from './planetSections';
import './command-center.css';

type CommandKind = 'chapter' | 'project' | 'note' | 'link';

interface CommandItem {
  id: string;
  label: string;
  description: string;
  kind: CommandKind;
  keywords: string;
  run: () => void;
}

function jumpToScene(index: number) {
  const scene = SCENES[index];
  if (!scene || typeof window === 'undefined') return;

  window.__planetModeController?.setMode('narrative');
  const max = document.documentElement.scrollHeight - window.innerHeight;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  window.scrollTo({
    top: Math.max(0, scene.start * max + 1),
    behavior: reduced ? 'auto' : 'smooth',
  });
}

function openDetail(type: 'project' | 'note', id: string | number) {
  if (typeof window === 'undefined') return;
  const detail = { type, id: String(id) };
  window.__planetOpenPanel = detail;
  window.dispatchEvent(
    new CustomEvent('planet:detail:open', { detail }),
  );
}

export function CommandCenter() {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const commands = useMemo<CommandItem[]>(
    () => [
      ...SCENES.map((scene, index) => ({
        id: `scene:${scene.key}`,
        label: scene.name,
        description: scene.title,
        kind: 'chapter' as const,
        keywords: `${scene.key} ${scene.name} ${scene.title} ${scene.subtitle}`,
        run: () => jumpToScene(index),
      })),
      ...SATELLITES.map((project) => ({
        id: `project:${project.id}`,
        label: project.name,
        description: `${project.year} · ${project.tech.join(', ')}`,
        kind: 'project' as const,
        keywords: `${project.name} ${project.blurb} ${project.tech.join(' ')}`,
        run: () => openDetail('project', project.id),
      })),
      ...NOTES.map((note) => ({
        id: `note:${note.id}`,
        label: note.title,
        description: `${note.date} · ${note.read}`,
        kind: 'note' as const,
        keywords: `${note.title} ${note.tag} ${note.tags?.join(' ') ?? ''} ${note.excerpt ?? ''}`,
        run: () => openDetail('note', note.id),
      })),
      {
        id: 'link:github',
        label: 'GitHub',
        description: 'Source code and open-source work',
        kind: 'link',
        keywords: 'github source code open source',
        run: () => window.open(CONTACT.github, '_blank', 'noopener,noreferrer'),
      },
      {
        id: 'link:email',
        label: 'Start a conversation',
        description: CONTACT.email,
        kind: 'link',
        keywords: 'email contact hello collaborate work together',
        run: () => {
          window.location.href = `mailto:${CONTACT.email}`;
        },
      },
      {
        id: 'link:rss',
        label: 'RSS feed',
        description: 'Follow new writing in your reader',
        kind: 'link',
        keywords: 'rss feed writing subscribe notes',
        run: () => {
          window.location.href = CONTACT.rss;
        },
      },
    ],
    [],
  );

  const matches = useMemo(() => {
    const terms = query.toLocaleLowerCase().trim().split(/\s+/).filter(Boolean);
    if (terms.length === 0) return commands;
    return commands.filter((command) => {
      const haystack = `${command.label} ${command.description} ${command.keywords}`.toLocaleLowerCase();
      return terms.every((term) => haystack.includes(term));
    });
  }, [commands, query]);

  const close = () => {
    setIsOpen(false);
    setQuery('');
    setActiveIndex(0);
    window.setTimeout(() => triggerRef.current?.focus(), 0);
  };

  const select = (command: CommandItem) => {
    setIsOpen(false);
    setQuery('');
    setActiveIndex(0);
    command.run();
  };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const isShortcut = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k';
      if (isShortcut) {
        event.preventDefault();
        setIsOpen((open) => !open);
        return;
      }
      if (event.key === 'Escape' && isOpen) {
        event.preventDefault();
        close();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    inputRef.current?.focus();
  }, [isOpen]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  const onInputKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => Math.min(index + 1, Math.max(0, matches.length - 1)));
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
    }
    if (event.key === 'Enter' && matches[activeIndex]) {
      event.preventDefault();
      select(matches[activeIndex]);
    }
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="command-center__trigger"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-controls="command-center"
        aria-keyshortcuts="Control+K Meta+K"
        onClick={() => setIsOpen(true)}
      >
        <span aria-hidden="true" className="command-center__trigger-mark">⌘</span>
        <span>Index</span>
        <kbd>⌘K</kbd>
      </button>

      {isOpen && (
        <div className="command-center" role="presentation">
          <button
            type="button"
            className="command-center__scrim"
            aria-label="Close site index"
            onClick={close}
          />
          <section
            id="command-center"
            className="command-center__dialog"
            role="dialog"
            aria-modal="true"
            aria-label="Site index"
          >
            <div className="command-center__head">
              <span className="command-center__eyebrow">yhazrin / index</span>
              <button type="button" onClick={close} className="command-center__close" aria-label="Close site index">
                Esc
              </button>
            </div>
            <label className="command-center__search">
              <span className="sr-only">Search this site</span>
              <span aria-hidden="true">⌕</span>
              <input
                ref={inputRef}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={onInputKeyDown}
                placeholder="Search chapters, work, notes…"
                autoComplete="off"
              />
            </label>
            <p className="command-center__hint">Use ↑ ↓ to move · Enter to open</p>
            <div className="command-center__results" role="listbox" aria-label="Search results">
              {matches.length > 0 ? matches.map((command, index) => (
                <button
                  key={command.id}
                  type="button"
                  role="option"
                  aria-selected={index === activeIndex}
                  className={['command-center__result', index === activeIndex ? 'is-active' : ''].join(' ')}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => select(command)}
                >
                  <span className={`command-center__kind command-center__kind--${command.kind}`} aria-hidden="true">
                    {command.kind === 'chapter' ? '○' : command.kind === 'project' ? '↗' : command.kind === 'note' ? '✦' : '→'}
                  </span>
                  <span className="command-center__copy">
                    <strong>{command.label}</strong>
                    <small>{command.description}</small>
                  </span>
                  <span className="command-center__type">{command.kind}</span>
                </button>
              )) : (
                <p className="command-center__empty">No match yet. Try “projects”, “notes”, or a tool name.</p>
              )}
            </div>
          </section>
        </div>
      )}
    </>
  );
}

export default CommandCenter;
